import React, { useState } from "react";
import { Page, withForm } from "components/common";
import { FormGroup, SaveButton, required } from "components/common/Forms";
import { compose, withState, withPropsOnChange } from "recompose";
import { Alert } from "reactstrap";
import { LocalForm, Control, actions, Errors } from "react-redux-form";
import { get } from "lodash";
import { Link } from "react-router-dom";
import classNames from "classnames";

// Define the passwordsMatch function
const passwordsMatch = ({ password, confirm_new_password }) => {
  if (!(password && confirm_new_password)) return true;
  return password === confirm_new_password;
};

// Define HelpText component
const HelpText = (props) => (
  <span className="form-text small text-danger" {...props} />
);

// Define AlertDanger component
const AlertDanger = (props) => <Alert color="danger">{props.children}</Alert>;

// Define ValidationList component
const ValidationList = ({ password }) => {
  const criteria = [
    {
      label: "Password must be at least 8 characters long.",
      test: (pwd) => pwd.length >= 8,
    },
    {
      label: "Password must contain at least one digit",
      test: (pwd) => /[0-9]/.test(pwd),
    },
  ];

  return (
    <div className="ml-2">
      <h6>Password must have</h6>
      <ul className="list-unstyled">
        {criteria.map(({ label, test }) => (
          <li
            key={label}
            className={classNames({
              "text-success": test(password),
              "text-danger": !test(password),
            })}
          >
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PasswordReset = ({
  item,
  form,
  setDispatch,
  onSubmit,
  onUpdate,
  onChange,
  dispatch,
  success,
  failure,
}) => {
  const [password, setPassword] = useState("");

  return (
    <Page>
      {success ? (
        <Alert color="success">
          <span className="fa fa-check mr-1" />
          Your password has been successfully updated, you can now{" "}
          <Link to="/login" replace>
            login
          </Link>
        </Alert>
      ) : (
        <div className="row">
          <div className="col-md-6">
            <h6> Password change form </h6>
            <LocalForm
              initialState={item}
              onSubmit={onSubmit}
              getDispatch={setDispatch}
              onUpdate={onUpdate}
              onChange={(form) => {
                onChange(form);
                setPassword(form.password);
              }}
              validators={{
                "": { passwordsMatch },
              }}
            >
              <FormGroup label="New Password *" model=".password">
                <Control.text
                  changeAction={(model, value) => {
                    dispatch(actions.change(model, value));
                    dispatch(actions.validate(model));
                    setPassword(value);
                  }}
                  type="password"
                  model=".password"
                  validators={{ required }}
                />
                <Errors
                  model=".password"
                  messages={{
                    required: "Password is required",
                    serverValidation:
                      "Password must be at least 8 characters and be a mix of letters and numbers",
                  }}
                  component={HelpText}
                />
              </FormGroup>
              <FormGroup label="Confirm Password *" model=".confirm_new_password">
                <Control.text
                  type="password"
                  model=".confirm_new_password"
                  validators={{ required }}
                />
                <Errors
                  model="local"
                  messages={{
                    required: "Confirm Password is required",
                    passwordsMatch: "Passwords do not match",
                  }}
                  component={HelpText}
                />
              </FormGroup>
              <Errors
                model="serverErrors"
                messages={{
                  InvalidToken: (
                    <span>
                      The requested link is either invalid or has already been used,
                      you can request a new one from the{" "}
                      <Link to="/forgotten_password" replace>
                        Forgotten Password
                      </Link>{" "}
                      page.
                    </span>
                  ),
                  TokenExpired: (
                    <span>
                      The requested link has expired, you can request a new one from
                      the{" "}
                      <Link to="/forgotten_password" replace>
                        Forgotten Password
                      </Link>{" "}
                      page.
                    </span>
                  ),
                }}
                wrapper={AlertDanger}
              />
              <div className="">
                <SaveButton className="w-100" form={form}>
                  Submit
                </SaveButton>
              </div>
            </LocalForm>
          </div>
          <div className="col-md-6">
            <div className="ml-4">
            <ValidationList password={password} />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

const enhance = compose(
  withState("success", "setSuccess", null),
  withState("failure", "setFailure", null),
  withPropsOnChange(["match"], ({ match }) => ({
    token: match.params.token,
  })),
  withForm({
    onSubmit:
      ({
        client,
        item: { password },
        token,
        setSuccess,
        setFailure,
        dispatch,
      }) =>
      () => {
        setSuccess(false);
        dispatch(actions.resetErrors("serverErrors"));

        return client
          .post("/password_reset", {
            token,
            password,
          })
          .then(() => {
            setSuccess(true);
          })
          .catch((err) => {
            const code = get(err, "response.data.code");

            if (code === "PasswordFailsValidation") {
              dispatch(
                actions.setErrors("local.password", {
                  serverValidation: true,
                })
              );
            } else if (code === "InvalidToken" || code === "TokenExpired") {
              dispatch(
                actions.setErrors("serverErrors", {
                  [code]: true,
                })
              );
            } else {
              throw err;
            }
          });
      },
  })
);

export default enhance(PasswordReset);